import { passwordProblems, type PasswordPolicy, type PasswordRule } from "@starsach/shared-types";

/**
 * The words first sign-in's screens build from data (gairm/lms#393): the rules a new password still needs, and the
 * authenticator's setup key grouped and spelled out. Kept apart from the screen so the tests read them directly.
 */

/** The rules a new password still needs, named in words: "Your new password still needs an upper-case letter and a symbol." */
export function stillNeeded(password: string, policy: PasswordPolicy): string | undefined {
  const problems = passwordProblems(password, policy);
  if (problems.length === 0) return undefined;
  const words: Record<PasswordRule, string> = {
    length: `at least ${policy.minLength} characters`,
    uppercase: "an upper-case letter",
    lowercase: "a lower-case letter",
    digit: "a number",
    symbol: "a symbol",
    "edge-space": "no space at the start or the end",
  };
  const names = problems.map((p) => words[p]);
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
  return `Your new password still needs ${list}.`;
}

/** Four characters a group, as authenticator apps show a key and people read one out. */
export function groupSecret(secret: string): string[] {
  return secret.match(/.{1,4}/g) ?? [];
}

/** The groups again, each character on its own, so a screen reader spells the key rather than guessing at words. */
export function spellSecret(secret: string): string {
  return groupSecret(secret)
    .map((g) => [...g].join(" "))
    .join(", ");
}
